package com.yogaaicoach.common.ui.components

import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.svg.ReactSVG.defs
import react.dom.svg.ReactSVG.linearGradient
import react.dom.svg.ReactSVG.path
import react.dom.svg.ReactSVG.rect
import react.dom.svg.ReactSVG.stop
import react.dom.svg.ReactSVG.svg
import react.useId
import web.dom.ElementId

// gradientTint(): Brush.linearGradient(colors = [primaryBlue @ 90%, lightBlueHighlight],
// start = Offset(0, height), end = Offset(width, 0)) — i.e. bottom-left to top-right.
// lightBlueHighlight = lerp(LightBlue #0096c7, White, 0.15) = #26a6cf.
// useId() gives each icon instance its own gradient id, since multiple instances of the same
// icon (e.g. AutoAwesomeIcon appears 3x on the onboarding page) would otherwise render duplicate
// SVG element ids in the DOM.
private fun ChildrenBuilder.GradientIconSvgCustom(width: Double, height: Double, viewBox: String, content: ChildrenBuilder.(gradientId: ElementId) -> Unit) {
    val gradientId = useId()
    svg {
        this.width = width; this.height = height; this.viewBox = viewBox
        defs {
            linearGradient {
                id = gradientId
                x1 = 0.0; y1 = 1.0; x2 = 1.0; y2 = 0.0
                stop { offset = "0%"; stopColor = "rgba(0,106,142,0.9)" }
                stop { offset = "100%"; stopColor = "#26a6cf" }
            }
        }
        content(gradientId)
    }
}

private fun ChildrenBuilder.GradientIconSvg(width: Double, height: Double, viewBox: String, paths: List<String>) {
    GradientIconSvgCustom(width, height, viewBox) { gradientId ->
        paths.forEach { pathData ->
            path { fill = "url(#$gradientId)"; d = pathData }
        }
    }
}

// Converted directly from the project's actual icons (fui_ic_googleg_color_24dp.xml /
// fui_ic_facebook_white_22dp.xml) — Android vector drawable pathData is SVG-path compatible.
internal val GoogleIcon = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 512 512"
        path { fill = "#4285f4"; d = "M482.6,261.4c0,-16.7 -1.5,-32.8 -4.3,-48.3H256v91.3h127c-5.5,29.5 -22.1,54.5 -47.1,71.2v59.2h76.3c44.6,-41.1 70.4,-101.6 70.4,-173.5z" }
        path { fill = "#34a853"; d = "M256,492c63.7,0 117.1,-21.1 156.2,-57.2l-76.3,-59.2c-21.1,14.2 -48.2,22.5 -79.9,22.5 -61.5,0 -113.5,-41.5 -132.1,-97.3H45.1v61.2c38.8,77.1 118.6,130 210.9,130z" }
        path { fill = "#fbbc05"; d = "M123.9,300.8c-4.7,-14.2 -7.4,-29.3 -7.4,-44.8s2.7,-30.7 7.4,-44.8V150H45.1C29.1,181.9 20,217.9 20,256c0,38.1 9.1,74.1 25.1,106l78.8,-61.2z" }
        path { fill = "#ea4335"; d = "M256,113.9c34.7,0 65.8,11.9 90.2,35.3l67.7,-67.7C373,43.4 319.6,20 256,20c-92.3,0 -172.1,52.9 -210.9,130l78.8,61.2c18.6,-55.8 70.6,-97.3 132.1,-97.3z" }
    }
}

internal val FacebookIcon = FC<Props> {
    svg {
        width = 22.0; height = 22.0; viewBox = "0 0 22 22"
        path {
            fill = "#ffffff"
            d = "M22,11c0,-6.075 -4.925,-11 -11,-11S0,4.925 0,11c0,5.494 4.023,10.035 9.25,10.84v-7.673H6.477V11H9.25V8.645c0,-2.754 1.64,-4.266 4.155,-4.266 1.19,0 2.444,0.212 2.444,0.212v2.71h-1.375c-1.356,0 -1.78,0.85 -1.78,1.71v2.09h3.08l-0.49,3.167h-2.59v7.673C17.977,21.035 22,16.494 22,11Z"
        }
    }
}

// Material Icons "Email" (filled, 24dp) — same icon as Icons.Default.Email used by SignInButtons.kt.
internal val EmailIcon = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#ffffff"
            d = "M20,4H4C2.9,4 2,4.9 2,6v12c0,1.1 0.9,2 2,2h16c1.1,0 2,-0.9 2,-2V6C22,4.9 21.1,4 20,4zM20,8l-8,5L4,8V6l8,5l8,-5V8z"
        }
    }
}

// Material Icons used by BenefitsStrip.kt: Icons.Default.CheckCircle, Icons.Default.Schedule,
// Icons.AutoMirrored.Filled.ShowChart — same tint (white @ 85% alpha) and size (40dp).
internal val CheckCircleIcon = FC<Props> {
    svg {
        width = 40.0; height = 40.0; viewBox = "0 0 24 24"
        path {
            fill = "rgba(255,255,255,0.85)"
            d = "M12,2C6.48,2 2,6.48 2,12s4.48,10 10,10 10,-4.48 10,-10S17.52,2 12,2zM10,17l-5,-5 1.41,-1.41L10,14.17l7.59,-7.59L19,8l-9,9z"
        }
    }
}

internal val ScheduleIcon = FC<Props> {
    svg {
        width = 40.0; height = 40.0; viewBox = "0 0 24 24"
        path {
            fill = "rgba(255,255,255,0.85)"
            d = "M11.99,2C6.47,2 2,6.48 2,12s4.47,10 9.99,10C17.52,22 22,17.52 22,12S17.52,2 11.99,2zM12,20c-4.42,0 -8,-3.58 -8,-8s3.58,-8 8,-8 8,3.58 8,8 -3.58,8 -8,8z"
        }
        path {
            fill = "rgba(255,255,255,0.85)"
            d = "M12.5,7H11v6l5.25,3.15 0.75,-1.23 -4.5,-2.67z"
        }
    }
}

internal val ShowChartIcon = FC<Props> {
    svg {
        width = 40.0; height = 40.0; viewBox = "0 0 24 24"
        path {
            fill = "rgba(255,255,255,0.85)"
            d = "M3.5,18.49l6,-6.01 4,4L22,6.92l-1.41,-1.41 -7.09,7.97 -4,-4L2,16.99z"
        }
    }
}

// Icons used by OnboardingScreen.kt / HeroCard.kt — all use Modifier.gradientTint() in Compose.
internal val TargetIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 960 960", listOf(
        "M480,880Q397,880 324,848.5Q251,817 197,763Q143,709 111.5,636Q80,563 80,480Q80,397 111.5,324Q143,251 197,197Q251,143 324,111.5Q397,80 480,80Q563,80 636,111.5Q709,143 763,197Q817,251 848.5,324Q880,397 880,480Q880,563 848.5,636Q817,709 763,763Q709,817 636,848.5Q563,880 480,880ZM480,800Q614,800 707,707Q800,614 800,480Q800,346 707,253Q614,160 480,160Q346,160 253,253Q160,346 160,480Q160,614 253,707Q346,800 480,800ZM480,720Q380,720 310,650Q240,580 240,480Q240,380 310,310Q380,240 480,240Q580,240 650,310Q720,380 720,480Q720,580 650,650Q580,720 480,720ZM480,640Q546,640 593,593Q640,546 640,480Q640,414 593,367Q546,320 480,320Q414,320 367,367Q320,414 320,480Q320,546 367,593Q414,640 480,640ZM480,560Q447,560 423.5,536.5Q400,513 400,480Q400,447 423.5,423.5Q447,400 480,400Q513,400 536.5,423.5Q560,447 560,480Q560,513 536.5,536.5Q513,560 480,560Z"
    ))
}

internal val AutoAwesomeIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M19,9l1.25,-2.75L23,5l-2.75,-1.25L19,1l-1.25,2.75L15,5l2.75,1.25L19,9z",
        "M19,15l-1.25,2.75L15,19l2.75,1.25L19,23l1.25,-2.75L23,19l-2.75,-1.25L19,15z",
        "M11.5,9.5L9,4L6.5,9.5L1,12l5.5,2.5L9,20l2.5,-5.5L17,12L11.5,9.5zM9.99,12.99L9,15.17l-0.99,-2.18L5.83,12l2.18,-0.99L9,8.83l0.99,2.18L12.17,12L9.99,12.99z"
    ))
}

// Same shape as AutoAwesomeIcon, white fill — for use on solid primary-colored backgrounds
// (e.g. the "Create My Program" CTA button), where the gradientTint-simplified blue fill above
// would be invisible/wrong-contrast.
internal val AutoAwesomeIconWhite = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#ffffff"
            d = "M19,9l1.25,-2.75L23,5l-2.75,-1.25L19,1l-1.25,2.75L15,5l2.75,1.25L19,9z"
        }
        path {
            fill = "#ffffff"
            d = "M19,15l-1.25,2.75L15,19l2.75,1.25L19,23l1.25,-2.75L23,19l-2.75,-1.25L19,15z"
        }
        path {
            fill = "#ffffff"
            d = "M11.5,9.5L9,4L6.5,9.5L1,12l5.5,2.5L9,20l2.5,-5.5L17,12L11.5,9.5zM9.99,12.99L9,15.17l-0.99,-2.18L5.83,12l2.18,-0.99L9,8.83l0.99,2.18L12.17,12L9.99,12.99z"
        }
    }
}

internal val CalendarMonthIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M19,4h-1V2h-2v2H8V2H6v2H5C3.89,4,3.01,4.9,3.01,6L3,20c0,1.1,0.89,2,2,2h14c1.1,0,2,-0.9,2,-2V6C21,4.9,20.1,4,19,4z M19,20H5V10h14V20z M9,14H7v-2h2V14z M13,14h-2v-2h2V14z M17,14h-2v-2h2V14z M9,18H7v-2h2V18z M13,18h-2v-2h2V18z M17,18h-2v-2h2V18z"
    ))
}

internal val CenterFocusStrongIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M12,8c-2.21,0,-4,1.79,-4,4s1.79,4,4,4 4,-1.79,4,-4 -1.79,-4,-4,-4z M5,15H3v4c0,1.1,0.9,2,2,2h4v-2H5V15z M5,5h4V3H5c-1.1,0,-2,0.9,-2,2v4h2V5z M19,3h-4v2h4v4h2V5c0,-1.1,-0.9,-2,-2,-2z M19,19h-4v2h4c1.1,0,2,-0.9,2,-2v-4h-2V19z"
    ))
}

// FreeTrialBanner.kt's icons use onTertiaryContainer (#31111d), not gradientTint.
internal val ChevronRightIcon = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path { fill = "#31111d"; d = "M10,6L8.59,7.41 13.17,12l-4.58,4.59L10,18l6,-6z" }
    }
}

internal val ChevronDownIcon = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path { fill = "#31111d"; d = "M7.41,8.59L12,13.17l4.59,-4.58L18,10l-6,6l-6,-6 1.41,-1.41z" }
    }
}

internal val InfoIcon = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#31111d"
            d = "M11,7h2v2h-2zM11,11h2v6h-2zM12,2C6.48,2 2,6.48 2,12s4.48,10 10,10 10,-4.48 10,-10S17.52,2 12,2z M12,20c-4.41,0,-8,-3.59,-8,-8s3.59,-8 8,-8 8,3.59 8,8 -3.59,8 -8,8z"
        }
    }
}

// Icons used by TopBar.kt's MenuDropdown.
internal val MenuIcon = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path { fill = "#2e2c28"; d = "M3,18h18v-2H3v2z M3,13h18v-2H3v2z M3,6v2h18V6H3z" }
    }
}

internal val HistoryIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M13,3c-4.97,0,-9,4.03,-9,9H1l3.89,3.89 0.07,0.14L9,12H6c0,-3.87,3.13,-7,7,-7s7,3.13,7,7 -3.13,7,-7,7c-1.93,0,-3.68,-0.79,-4.94,-2.06l-1.42,1.42C8.27,19.99,10.51,21,13,21c4.97,0,9,-4.03,9,-9s-4.03,-9,-9,-9z M12,8v5l4.28,2.54 0.72,-1.21 -3.5,-2.08V8H12z"
    ))
}

internal val CreditCardIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M20,4H4c-1.11,0,-1.99,0.89,-1.99,2L2,18c0,1.11,0.89,2,2,2h16c1.11,0,2,-0.89,2,-2V6c0,-1.11,-0.89,-2,-2,-2z M20,18H4v-6h16v6z M20,8H4V6h16v2z"
    ))
}

internal val ContactEmailIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M20,4H4C2.9,4 2,4.9 2,6v12c0,1.1 0.9,2 2,2h16c1.1,0 2,-0.9 2,-2V6C22,4.9 21.1,4 20,4zM20,8l-8,5L4,8V6l8,5l8,-5V8z"
    ))
}

internal val LogoutIcon = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#b00020"
            d = "M17,7l-1.41,1.41L18.17,11H8v2h10.17l-2.58,2.58L17,17l5,-5z M4,5h8V3H4c-1.1,0,-2,0.9,-2,2v14c0,1.1,0.9,2,2,2h8v-2H4V5z"
        }
    }
}

// Icons used by ProgramDashboardScreen.kt (NextWorkoutContainer/SummaryComponent/TimelineColumn).
internal val SportsGymnasticsIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M4,6c0,-1.1,0.9,-2,2,-2s2,0.9,2,2S7.1,8,6,8S4,7.1,4,6z M1,9h6l7,-5l1.31,1.52L11.14,8.5H14L21.8,4L23,5.4L14.5,12L14,22h-2 l-0.5,-10L8,11H1V9z"
    ))
}

internal val BarChartIcon = FC<Props> {
    GradientIconSvgCustom(24.0, 24.0, "0 0 24 24") { gradientId ->
        rect { fill = "url(#$gradientId)"; x = 4.0; y = 9.0; width = 4.0; height = 11.0 }
        rect { fill = "url(#$gradientId)"; x = 16.0; y = 13.0; width = 4.0; height = 7.0 }
        rect { fill = "url(#$gradientId)"; x = 10.0; y = 4.0; width = 4.0; height = 16.0 }
    }
}

internal val DateRangeIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M9,11H7v2h2v-2z M13,11h-2v2h2v-2z M17,11h-2v2h2v-2z M19,4h-1V2h-2v2H8V2H6v2H5c-1.11,0,-1.99,0.9,-1.99,2L3,20c0,1.1,0.89,2,2,2h14c1.1,0,2,-0.9,2,-2V6c0,-1.1,-0.9,-2,-2,-2z M19,20H5V9h14v11z"
    ))
}

internal val FitnessCenterIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M20.57,14.86L22,13.43 20.57,12 17,15.57 8.43,7 12,3.43 10.57,2 9.14,3.43 7.71,2 5.57,4.14 4.14,2.71 2.71,4.14l1.43,1.43L2,7.71l1.43,1.43L2,10.57 3.43,12 7,8.43 15.57,17 12,20.57 13.43,22l1.43,-1.43L16.29,22l2.14,-2.14 1.43,1.43 1.43,-1.43 -1.43,-1.43L22,16.29z"
    ))
}

internal val AccessTimeIcon = FC<Props> {
    GradientIconSvg(24.0, 24.0, "0 0 24 24", listOf(
        "M11.99,2C6.47,2 2,6.48 2,12s4.47,10 9.99,10C17.52,22 22,17.52 22,12S17.52,2 11.99,2zM12,20c-4.42,0 -8,-3.58 -8,-8s3.58,-8 8,-8 8,3.58 8,8 -3.58,8 -8,8z",
        "M12.5,7H11v6l5.25,3.15 0.75,-1.23 -4.5,-2.67z"
    ))
}

// TimelineColumn.kt's "Completed" checkmark uses a solid onSecondaryContainer tint, not
// gradientTint.
internal val CheckIcon = FC<Props> {
    svg {
        width = 16.0; height = 16.0; viewBox = "0 0 24 24"
        path { fill = "#000000"; d = "M9,16.17L4.83,12l-1.42,1.41L9,19 21,7l-1.41,-1.41z" }
    }
}

internal val PlayArrowIcon = FC<Props> {
    GradientIconSvg(16.0, 16.0, "0 0 24 24", listOf("M8,5v14l11,-7z"))
}

// SubscriptionLockedOverlay.kt's Icon uses tint=primary directly, no .gradientTint() modifier —
// a solid fill, unlike most other icons in this file.
internal val StarsIcon = FC<Props> {
    svg {
        width = 64.0; height = 64.0; viewBox = "0 0 24 24"
        path {
            fill = "#006a8e"
            d = "M11.99,2C6.47,2 2,6.48 2,12s4.47,10 9.99,10C17.52,22 22,17.52 22,12S17.52,2 11.99,2zm4.24,16L12,15.45 7.77,18l1.12,-4.81 -3.73,-3.23 4.92,-0.42L12,5l1.92,4.53 4.92,0.42 -3.73,3.23L16.23,18z"
        }
    }
}

// StepProgressIndicator.kt's step icons (TrendingUp, Person) and its past-step checkmark all use
// .gradientTint(), unlike the timeline's solid-black CheckIcon.
internal val TrendingUpIcon = FC<Props> {
    GradientIconSvg(16.0, 16.0, "0 0 24 24", listOf("M16,6l2.29,2.29 -4.88,4.88 -4,-4L2,16.59 3.41,18l6,-6 4,4 6.3,-6.29L22,12V6z"))
}

internal val PersonIcon = FC<Props> {
    GradientIconSvg(16.0, 16.0, "0 0 24 24", listOf("M12,12c2.21,0 4,-1.79 4,-4s-1.79,-4 -4,-4 -4,1.79 -4,4 1.79,4 4,4zm0,2c-2.67,0 -8,1.34 -8,4v2h16v-2c0,-2.66 -5.33,-4 -8,-4z"))
}

internal val CheckIconGradient = FC<Props> {
    GradientIconSvg(16.0, 16.0, "0 0 24 24", listOf("M9,16.17L4.83,12l-1.42,1.41L9,19 21,7l-1.41,-1.41z"))
}

// ── Payment / subscription icons ───────────────────────────────────────────

// StatusMessage(type=SUCCESS): green CheckCircle, 24dp (same shape as the white/40dp version above).
internal val CheckCircleIconGreen = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#2a7d4e"
            d = "M12,2C6.48,2 2,6.48 2,12s4.48,10 10,10 10,-4.48 10,-10S17.52,2 12,2zM10,17l-5,-5 1.41,-1.41L10,14.17l7.59,-7.59L19,8l-9,9z"
        }
    }
}

// StatusMessage(type=CANCEL): red Cancel icon, 24dp.
internal val CancelIconRed = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#b00020"
            d = "M12,2C6.47,2 2,6.47 2,12s4.47,10 10,10 10,-4.47 10,-10S17.53,2 12,2zm5,13.59L15.59,17 12,13.41 8.41,17 7,15.59 10.59,12 7,8.41 8.41,7 12,10.59 15.59,7 17,8.41 13.41,12 17,15.59z"
        }
    }
}

// ManageSubscriptionScreen's "cancelled" warning — Icons.Outlined.Warning, error tint, 24dp.
internal val WarningIconRed = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path { fill = "#b00020"; d = "M1,21h22L12,2 1,21zm12,-3h-2v-2h2v2zm0,-4h-2v-4h2v4z" }
    }
}

// ManageSubscriptionScreen's info rows: Icons.Outlined.CreditCard/DateRange, primary tint, 24dp.
internal val CreditCardIconPrimary = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#006a8e"
            d = "M20,4H4c-1.11,0,-1.99,0.89,-1.99,2L2,18c0,1.11,0.89,2,2,2h16c1.11,0,2,-0.89,2,-2V6c0,-1.11,-0.89,-2,-2,-2z M20,18H4v-6h16v6z M20,8H4V6h16v2z"
        }
    }
}

internal val DateRangeIconPrimary = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#006a8e"
            d = "M9,11H7v2h2v-2z M13,11h-2v2h2v-2z M17,11h-2v2h2v-2z M19,4h-1V2h-2v2H8V2H6v2H5c-1.11,0,-1.99,0.9,-1.99,2L3,20c0,1.1,0.89,2,2,2h14c1.1,0,2,-0.9,2,-2V6c0,-1.1,-0.9,-2,-2,-2z M19,20H5V9h14v11z"
        }
    }
}

// DescriptionDialogButton's Icon uses tint=primary directly — solid fill, not gradientTint().
internal val InfoIconPrimary = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#006a8e"
            d = "M12,2C6.48,2 2,6.48 2,12s4.48,10 10,10 10,-4.48 10,-10S17.52,2 12,2zm1,15h-2v-6h2v6zm0,-8h-2V7h2v2z"
        }
    }
}

// PrimaryButton's icon defaults to isSelected=true -> iconTint=onPrimary (white).
internal val ArrowForwardIconWhite = FC<Props> {
    svg {
        width = 24.0; height = 24.0; viewBox = "0 0 24 24"
        path {
            fill = "#ffffff"
            d = "M12,4l-1.41,1.41L16.17,11H4v2h12.17l-5.58,5.59L12,20l8,-8z"
        }
    }
}

internal val ArrowForwardIconPrimary = FC<Props> {
    svg {
        width = 20.0; height = 20.0; viewBox = "0 0 24 24"
        path {
            fill = "#006a8e"
            d = "M12,4l-1.41,1.41L16.17,11H4v2h12.17l-5.58,5.59L12,20l8,-8z"
        }
    }
}

internal val EditIconGradient = FC<Props> {
    GradientIconSvg(18.0, 18.0, "0 0 24 24", listOf(
        "M3,17.25V21h3.75L17.81,9.94l-3.75,-3.75L3,17.25zM20.71,7.04c0.39,-0.39 0.39,-1.02 0,-1.41l-2.34,-2.34c-0.39,-0.39 -1.02,-0.39 -1.41,0l-1.83,1.83 3.75,3.75 1.83,-1.83z"
    ))
}

// ── Workout screen controls ─────────────────────────────────────────────────

internal val SkipPreviousIcon = FC<Props> {
    svg {
        width = 28.0; height = 28.0; viewBox = "0 0 24 24"
        path { fill = "currentColor"; d = "M6 6h2v12H6zm3.5 6 8.5 6V6z" }
    }
}

internal val SkipNextIcon = FC<Props> {
    svg {
        width = 28.0; height = 28.0; viewBox = "0 0 24 24"
        path { fill = "currentColor"; d = "M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" }
    }
}

internal val PauseCircleIcon = FC<Props> {
    svg {
        width = 52.0; height = 52.0; viewBox = "0 0 24 24"
        path { fill = "currentColor"; d = "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" }
    }
}

// Filled circle with transparent play-arrow cutout — works with the default nonzero fill rule.
// The outline variant (donut + arrow) requires fill-rule=evenodd which the React SVG DSL does
// not expose as a typed property; this filled variant is visually equivalent at this size.
internal val PlayCircleIcon = FC<Props> {
    svg {
        width = 52.0; height = 52.0; viewBox = "0 0 24 24"
        path { fill = "currentColor"; d = "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" }
    }
}
